import React, {
  FormEvent,
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import {
  ProjectSubTitle,
  ContentContainer,
  ContentDescription,
  ContentGroup,
  ContentTitle,
  ContextContentContainer,
  MainContentGroup,
  MainImage,
  ProjectPageContainer,
  ProjectPageMainContent,
  ProjectTitle,
  TeamContentList,
  TeamContentListItem,
  ProjectTitleGroup,
  ProjectPageSubContent,
  AccessBackdrop,
  AccessButton,
  AccessCopy,
  AccessDialog,
  AccessError,
  AccessEyebrow,
  AccessForm,
  AccessHomeLink,
  AccessInput,
  AccessTitle,
  ProtectedPreview,
} from "./style";

import { ProjectDetailProps } from "../../data/project/props";

import NavbarSection from "../../components/pages/home/navbar-section";
import FooterSection from "../../components/pages/home/footer-section";
import PageMeta from "../../components/common/page-meta";

import {
  getProtectedRoutes,
  loginWithPortfolioPassword,
} from '../../data/firebase';
import { AuthenticationContext } from "../../context/authentication-context";

const Project: React.FC<ProjectDetailProps> = (props) => {
  const location = useLocation();

  const [scrollBar, setScrollBar] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isProtected, setProtected] = useState(Boolean(props.passwordProtected));
  const [isAccessDialogOpen, setAccessDialogOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [hasPasswordError, setPasswordError] = useState(false);
  const [isSubmittingPassword, setSubmittingPassword] = useState(false);
  const gateRef = useRef<HTMLDivElement | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const { isLoggedIn, isAuthenticationReady } = useContext(AuthenticationContext);

  useEffect(() => {
    const updateScrollProgress = () => {
      const bodyScrollTop = document.body.scrollTop;
      const { scrollTop, scrollHeight, clientHeight } =
        document.documentElement;
      const scrollableHeight = scrollHeight - clientHeight;
      const percentage = scrollableHeight > 0
        ? ((scrollTop + bodyScrollTop) / scrollableHeight) * 100
        : 0;
      setScrollBar(percentage);
    };

    window.addEventListener("scroll", updateScrollProgress, { passive: true });
    updateScrollProgress();

    return () => window.removeEventListener("scroll", updateScrollProgress);
  }, []);

  useEffect(() => {
    if (!isAuthenticationReady) {
      return;
    }

    if (isLoggedIn) {
      setLoading(false);
      setAccessDialogOpen(false);
      return;
    }

    let isCurrentPage = true;

    getProtectedRoutes().then((routes) => {
      if (!isCurrentPage) {
        return;
      }

      const protectedPage =
        Boolean(props.passwordProtected) || routes.includes(location.pathname);
      setProtected(protectedPage);
      setLoading(false);
    });

    return () => {
      isCurrentPage = false;
    };
  }, [isAuthenticationReady, isLoggedIn, location.pathname, props.passwordProtected]);

  useEffect(() => {
    const isLocked = isProtected && !isLoggedIn;
    if (loading || !isLocked || isAccessDialogOpen) {
      return;
    }

    const revealAccessDialog = () => {
      const gate = gateRef.current;
      if (!gate || window.scrollY < 24) {
        return;
      }

      if (gate.getBoundingClientRect().top <= window.innerHeight * 0.8) {
        setAccessDialogOpen(true);
      }
    };

    window.addEventListener("scroll", revealAccessDialog, { passive: true });
    revealAccessDialog();

    return () => window.removeEventListener("scroll", revealAccessDialog);
  }, [isAccessDialogOpen, isLoggedIn, isProtected, loading]);

  useEffect(() => {
    if (isAccessDialogOpen) {
      passwordInputRef.current?.focus();
    }
  }, [isAccessDialogOpen]);

  const submitPassword = async (event: FormEvent) => {
    event.preventDefault();

    if (!password || isSubmittingPassword) {
      setPasswordError(true);
      return;
    }

    setSubmittingPassword(true);
    setPasswordError(false);

    try {
      await loginWithPortfolioPassword(password);
      setPassword("");
    } catch (error) {
      setPasswordError(true);
    } finally {
      setSubmittingPassword(false);
    }
  };

  const renderProjectContent = (
    content: ProjectDetailProps["contents"][number],
    index: number
  ) => (
    <Fragment key={`${content.left.title}-${index}`}>
      <ProjectPageSubContent>
        <ContentGroup isNotHavingOneChildren={!!content.right}>
          <ContentContainer isFull={!!!content.right} isShown={true}>
            <ContentTitle isVisible={!!content.left.title}>
              {content.left.title}
            </ContentTitle>
            <ContentDescription isHavingMargin={!!content.left?.title}>
              {content.left.content}
            </ContentDescription>
          </ContentContainer>
          {content.right && (
            <ContentContainer
              isFull={!!content.right}
              isShown={!!content.right}
            >
              <ContentTitle isVisible={!!content.right?.title}>
                {content.right?.title || content.left.title}
              </ContentTitle>
              <ContentDescription isHavingMargin={!!content.right?.title}>
                {content.right?.content}
              </ContentDescription>
            </ContentContainer>
          )}
        </ContentGroup>
      </ProjectPageSubContent>
      {content.bottomImage && (
        <MainImage src={content.bottomImage} alt={`${props.title} project visual`} />
      )}
      {content.bottomContent}
    </Fragment>
  );

  const renderAccessDialog = () => {
    if (!isProtected || isLoggedIn) {
      return null;
    }

    return (
      <AccessBackdrop isVisible={isAccessDialogOpen} aria-hidden={!isAccessDialogOpen}>
        <AccessDialog
          role="region"
          aria-labelledby="portfolio-access-title"
          aria-describedby="portfolio-access-description"
        >
          <AccessEyebrow>Protected project</AccessEyebrow>
          <AccessTitle id="portfolio-access-title">
            Continue reading this project
          </AccessTitle>
          <AccessCopy id="portfolio-access-description">
            This case study is available to selected readers. Enter the
            portfolio password to unlock the full story.
          </AccessCopy>
          <AccessForm onSubmit={submitPassword}>
            <AccessInput
              ref={passwordInputRef}
              hasError={hasPasswordError}
              type="password"
              value={password}
              placeholder="Portfolio password"
              aria-label="Portfolio password"
              aria-invalid={hasPasswordError}
              autoComplete="current-password"
              onChange={(event) => {
                setPassword(event.target.value);
                setPasswordError(false);
              }}
            />
            <AccessButton
              type="submit"
              disabled={isSubmittingPassword || !password}
            >
              {isSubmittingPassword ? "Checking password…" : "Unlock project"}
            </AccessButton>
            {hasPasswordError && (
              <AccessError role="alert">
                That password did not work. Please try again.
              </AccessError>
            )}
          </AccessForm>
          <AccessHomeLink to="/">Back to homepage</AccessHomeLink>
        </AccessDialog>
      </AccessBackdrop>
    );
  };

  const renderPage = () => {
    const isLocked = isProtected && !isLoggedIn;
    const visibleContents = isLocked ? props.contents.slice(0, 1) : props.contents;
    const protectedPreview = props.contents[1] || props.contents[0];

    return (
      <>
        <ProjectPageMainContent isHavingPadding={!!props.context}>
          <ProjectTitleGroup>
            <ProjectTitle>{props.title}</ProjectTitle>
            <ProjectSubTitle>{props.timeline}</ProjectSubTitle>
          </ProjectTitleGroup>
          {props.context && (
            <MainContentGroup isNotHavingOneChildren={true}>
              <ContextContentContainer
                isFull={props.team.length === 0}
                isShown={true}
              >
                <ContentTitle isVisible>Context</ContentTitle>
                <ContentDescription isHavingMargin={true}>
                  {props.context}
                </ContentDescription>
              </ContextContentContainer>
              {props.team.length !== 0 && (
                <TeamContentList>
                  <ContentTitle isVisible={props.team.length !== 0}>
                    Team
                  </ContentTitle>
                  <ContentDescription isHavingMargin={true}>
                    {props.team.map((member) => (
                      <TeamContentListItem key={member}>
                        {member}
                      </TeamContentListItem>
                    ))}
                  </ContentDescription>
                </TeamContentList>
              )}
            </MainContentGroup>
          )}
        </ProjectPageMainContent>
        {props.thumbnail && (
          <MainImage src={props.thumbnail} alt={`${props.title} project thumbnail`} />
        )}
        {visibleContents.map(renderProjectContent)}
        {isLocked && protectedPreview && (
          <div ref={gateRef}>
            <ProtectedPreview aria-hidden="true">
              {renderProjectContent(protectedPreview, 1)}
            </ProtectedPreview>
            {renderAccessDialog()}
          </div>
        )}
        {!isLocked && <FooterSection />}
      </>
    );
  };

  return (
    <ProjectPageContainer>
      <PageMeta
        title={`${props.title} — Freddy's`}
        description={props.context || `${props.title} case study by Freddy Albert Sanjaya.`}
      />
      <NavbarSection percentage={scrollBar} showLine={true} />
      {!loading && renderPage()}
    </ProjectPageContainer>
  );
};

export default Project;
